/* eslint-disable max-len */
import {COLOR_THEME} from '@constant/game';
import {DESKTOP_BREAKPOINT, TABLET_BREAKPOINT} from '@constant/system';
import useCommon from '@utils/hooks/common';

const merge = (target, source) => {
  if (typeof target === 'undefined') {
    return source;
  }
  if (typeof source === 'undefined') {
    return target;
  }
  // Iterate through `source` properties and if an `Object` set property to merge of `target` and `source` properties
  for (const key of Object.keys(source)) {
    if (source[key] instanceof Object) Object.assign(source[key], merge(target[key], source[key]));
  }

  // Join `target` and modified `source`
  const newTarget = {
    ...target,
  };
  Object.assign(newTarget || {}, source);
  return newTarget;
};

const purpleTheme = {
  // TODO: set 2x background
  gameRoomBackground:
    // eslint-disable-next-line max-len
    'radial-gradient(#C7C7C7 0%, #969696 10%, #000000 75%)',
  moneyBackground: '#2A27B9 0% 0% no-repeat padding-box',
  tableImgPath:
    '/images-mobile/game-room/tables/purple_table.webp 1x, images-mobile/game-room/tables/purple_table@2x.webp 2x',
  tableImgPathAllIn:
    '/images-mobile/game-room/tables/purple_table_all_in.webp 1x, images-mobile/game-room/tables/purple_table_all_in@2x.webp 2x',
  myAmountBackgroundColor: '#2A27B9',
  topLeftPlayerBackground:
    '/images-mobile/game-room/tables/top_left_background.webp 1x, images-mobile/game-room/tables/top_left_background@2x.webp 2x',
  middleLeftPlayerBackground:
    '/images-mobile/game-room/tables/middle_left_background.webp 1x, images-mobile/game-room/tables/middle_left_background@2x.webp 2x',
  bottomLeftPlayerBackground:
    '/images-mobile/game-room/tables/bottom_left_background.webp 1x, images-mobile/game-room/tables/bottom_left_background@2x.webp 2x',
  bottomMiddlePlayerBackground:
    '/images-mobile/game-room/tables/bottom_middle_background.webp 1x, images-mobile/game-room/tables/bottom_middle_background@2x.webp 2x',
  middleRightPlayerBackground:
    '/images-mobile/game-room/tables/middle_right_background.webp 1x, images-mobile/game-room/tables/middle_right_background@2x.webp 2x',
};

const goldenTheme = {
  gameRoomBackground:
    // eslint-disable-next-line max-len
    'radial-gradient(#C7C7C7 0%, #969696 10%, #000000 75%)',
  moneyBackground: '#EE8315 0% 0% no-repeat padding-box',
  tableImgPath:
    '/images-mobile/game-room/tables/golden_table.webp 1x, images-mobile/game-room/tables/golden_table@2x.webp 2x',
  tableImgPathAllIn:
    '/images-mobile/game-room/tables/purple_table_all_in.webp 1x, images-mobile/game-room/tables/purple_table_all_in@2x.webp 2x',
  myAmountBackgroundColor: '#EE8315',
  topLeftPlayerBackground:
    '/images-mobile/game-room/tables/golden_top_left.webp 1x, images-mobile/game-room/tables/golden_top_left@2x.webp 2x',
  middleLeftPlayerBackground:
    '/images-mobile/game-room/tables/golden_middle_left.webp 1x, images-mobile/game-room/tables/golden_middle_left@2x.webp 2x',
  bottomLeftPlayerBackground:
    '/images-mobile/game-room/tables/golden_bottom_left.webp 1x, images-mobile/game-room/tables/golden_bottom_left@2x.webp 2x',
  bottomMiddlePlayerBackground:
    '/images-mobile/game-room/tables/golden_bottom_middle.webp 1x, images-mobile/game-room/tables/golden_bottom_middle@2x.webp 2x',
  middleRightPlayerBackground:
    '/images-mobile/game-room/tables/golden_middle_right.webp 1x, images-mobile/game-room/tables/golden_middle_right@2x.webp 2x',
};

const greenTheme = {
  gameRoomBackground:
    // eslint-disable-next-line max-len
    'radial-gradient(#C7C7C7 0%, #969696 10%, #000000 75%)',
  moneyBackground: '#1B915A 0% 0% no-repeat padding-box',
  tableImgPath: '/images-mobile/game-room/tables/classic-green-table.webp 1x',
  // tableImgPathAllIn: '/images-mobile/game-room/tables/classic_green_table_all_in_light.webp',
  tableImgPathAllIn: '/images-mobile/game-room/tables/horizontal/classic/red-table-2-2.webp',
  myAmountBackgroundColor: '#1B915A',
};

const getGameRoomTheme = (userTheme: string) => {
  if (userTheme === COLOR_THEME.PURPLE) return purpleTheme;
  if (userTheme === COLOR_THEME.GOLDEN) return goldenTheme;
  if (userTheme === COLOR_THEME.GREEN) return greenTheme;
  return greenTheme;
};

const goldGradientTextStyles = {
  background: 'linear-gradient(135deg, #FCF295 0%, #8F6A2B 100%)',
  '-webkit-background-clip': 'text',
  '-webkit-text-fill-color': 'transparent',
};

const theme = (userTheme: string) => ({
  colorPrimary: 'green',
  backgroundColor: '#222835',
  contentBackground: '#222835',
  desktopContentBackground: '#202633',
  inputBackground: '#383C47',
  gameRoomBackground: '#202633',
  borderColor: '#383D46',
  textColor: 'white',
  tabTitleTextStyles: {color: '#FFF', fontSize: '20px', fontWeight: '700'},
  secondaryTextColor: 'rgba(255, 255, 255, 0.5)',
  secondaryTextStyles: {color: 'rgba(255, 255, 255, 0.5)', fontWeight: '400', fontSize: 15},
  betTextColor: '#FAD610',
  linkColor: 'rgba(59, 153, 252, 1)',
  goldGradientTextStyles,
  styleFormat: [
    {
      id: 'desktop',
      width: 1920,
      changePoint: DESKTOP_BREAKPOINT,
    },
    {
      id: 'mobile',
      width: 375,
      changePoint: 0,
    },
  ],
  gameRoomStyles: {
    ...getGameRoomTheme(userTheme),
  },
});

const colorTheme = {
  dark: {},
  light: {},
};

export const getCurrentStyleFormat = screenWidth => {
  const {styleFormat} = theme(null);
  for (let i = 0; i < styleFormat.length; i += 1) {
    const {changePoint} = styleFormat[i];
    if (screenWidth > changePoint) {
      return {
        ...styleFormat[i],
        screenWidth,
      };
    }
  }
  return null;
};

interface Options {
  withUnit?: boolean;
  withLimit?: boolean;
}

export const getTheme = (styleFormat, userTheme: string) => {
  const toVW = (size, options?: Options) => {
    const {withUnit, withLimit = true} = options || {};
    if (styleFormat) {
      const {width, screenWidth} = styleFormat;
      if (screenWidth < width || !withLimit) {
        return `${(size / styleFormat.width) * 100}vw`;
      }
    }
    return withUnit ? `${size}px` : size;
  };

  const commonClass = {
    '@font-face': [
      {
        fontFamily: 'TestFront',
        src: 'url(/font/test.otf)',
      },
    ],
  };

  return {
    ...theme(userTheme),
    toVW,
    commonClass,
    classesMerge: merge,
    currentStyleFormat: styleFormat,
  };
};
